import React, { useCallback, useEffect, useMemo, useState } from "react";
import { SortType } from "../../../models/SortType";
import { Box, CircularProgress, Container, Paper, Badge } from "@mui/material";
import CustomTypography from "../../components/common/form/CustomTypography";
import AppButton from "../../components/common/buttons/AppButton";
import CustomTextField from "../../components/common/textFields/TextField";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import { useStores } from "../../../use-stores";
import { useLocation, useNavigate } from "react-router-dom";
import { useNotification } from "../../../utils/NotificationsContext";
import FilterSidebar from "../../components/common/filter/FilterSidebar";
import { FilterSidebarMenu, FilterMenuType } from "../../../models/FilterMenu";
import { ModuleType } from "../../../models/ModuleType";
import { FilterValue } from "../../../models/Filters/FilterValue";
import dayjs from "dayjs";
import { EmployeeRoles } from "../../../models/Employees/EmployeeRoles";
import PermissionBlockLabel from "../../components/common/pop-up/PermissionBlockLabel ";
import { PurhcaseRequestFilters } from "../../../models/PurchaseRequest/PurchaseRequestDetails";
import PrListingTable from "../../components/purchase-request/Prlisting";
import { PurchaseRequestRejectedBy } from "../../../models/PurchaseRequest/PurchaseRequestBody";
import {
  PurchaseRequestApprovalTeam,
  PurchaseRequestApprovedType,
  PurchaseRequestPendingApprovalType,
  PURCHASE_REQUEST_APPROVED_OPTIONS,
  PURCHASE_REQUEST_PENDING_APPROVAL_OPTIONS,
  parsePurchaseRequestApprovedTypes,
  parsePurchaseRequestPendingApprovalTypes,
} from "../../../models/PurchaseRequest/PurchaseRequestApprovalType";
import ConfirmationPopup from "../../components/common/pop-up/ConfirmationPopup";
import { UserStore } from "../../../stores/UserStore";

const PurchaseRequest: React.FC = () => {
  const { userStore, purchaseRequestStore } = useStores();
  const { showError, showSuccess } = useNotification();
  const [filterCount, setFilterCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [openFilter, setOpenFilter] = useState<boolean>(false);
  const [approvePopup, setApprovePopup] = useState<{
    open: boolean;
    prId: number | null;
    team: PurchaseRequestRejectedBy | null;
  }>({ open: false, prId: null, team: null });
  const [rejectPopup, setRejectPopup] = useState<{
    open: boolean;
    prId: number | null;
    team: PurchaseRequestRejectedBy | null;
  }>({ open: false, prId: null, team: null });
  const [cancelPRPopup, setCancelPRPopup] = useState<{
    open: boolean;
    prId: number | null;
  }>({ open: false, prId: null });
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);

  const filterFields: (keyof PurhcaseRequestFilters)[] = [
    "pr_created_by_admin_ids",
    "order_id",
    "created_date_from",
    "expected_payment_date_from",
    "pending_approval_type",
    "approved_type",
    "pr_status",
  ];

  const PR_STATUS_OPTIONS: FilterValue[] = [
    { id: "1", value: "Active" },
    { id: "0", value: "Cancelled" },
    { id: "2", value: "Rejected" },
  ];

  const parseUrlParams = (): PurhcaseRequestFilters => {
    const parseFilterArray = (param: string | null): FilterValue[] => {
      if (!param) return [];
      return param.split(",").map((item) => {
        const [id, value] = item.split(":");
        return { id, value };
      });
    };

    const parseDateParam = (param: string | null): dayjs.Dayjs | undefined => {
      if (!param) return undefined;
      const date = dayjs(param);
      return date.isValid() ? date : undefined;
    };

    return {
      pr_created_by_admin_ids: parseFilterArray(
        searchParams.get("pr_created_by_admin_ids")
      ),
      order_id: parseFilterArray(searchParams.get("order_id")),
      pr_status: parseFilterArray(searchParams.get("pr_status")),
      page_no: parseInt(searchParams.get("page_no") || "1", 10),
      search_text: searchParams.get("search_text") || undefined,
      limit: parseInt(searchParams.get("limit") || "30", 10),
      sort_by: searchParams.get("sort_by") || "created_date",
      sort_type:
        searchParams.get("sort_type") === "ASC" ? SortType.ASC : SortType.DESC,
      created_date_from: parseDateParam(searchParams.get("created_date_from")),
      created_date_to: parseDateParam(searchParams.get("created_date_to")),
      expected_payment_date_from: parseDateParam(
        searchParams.get("expected_payment_date_from")
      ),
      expected_payment_date_to: parseDateParam(
        searchParams.get("expected_payment_date_to")
      ),
      pending_approval_type: (() => {
        const fromParam = parsePurchaseRequestPendingApprovalTypes(
          searchParams.get("pending_approval_type"),
        );
        if (fromParam.length > 0) return fromParam;

        return [
          `${PurchaseRequestApprovalTeam.FINANCE}_pending`,
          `${PurchaseRequestApprovalTeam.SOURCING}_pending`,
          `${PurchaseRequestApprovalTeam.SWAGEAZY}_pending`,
        ].filter(
          (type): type is PurchaseRequestPendingApprovalType =>
            searchParams.get(type) === "true",
        );
      })(),
      approved_type: (() => {
        const fromParam = parsePurchaseRequestApprovedTypes(
          searchParams.get("approved_type"),
        );
        if (fromParam.length > 0) return fromParam;

        return [
          `${PurchaseRequestApprovalTeam.FINANCE}_approved`,
          `${PurchaseRequestApprovalTeam.SOURCING}_approved`,
          `${PurchaseRequestApprovalTeam.SWAGEAZY}_approved`,
        ].filter(
          (type): type is PurchaseRequestApprovedType =>
            searchParams.get(type) === "true",
        );
      })(),
    };
  };

  const initialParams: PurhcaseRequestFilters = parseUrlParams();

  const [filters, setFilters] = useState<PurhcaseRequestFilters>(initialParams);
  const [params, setParams] = useState<PurhcaseRequestFilters>(initialParams);
  const [searchTerm, setSearchTerm] = useState(
    searchParams.get("search_text") || ""
  );

  const handleFilterCount = useCallback(() => {
    const count = filterFields.reduce((count, field) => {
      const value = filters[field];
      if (Array.isArray(value) && value.length > 0) {
        return count + 1;
      } else if (
        value &&
        (field === "created_date_from" ||
          field === "created_date_to" ||
          field === "expected_payment_date_from" ||
          field === "expected_payment_date_to")
      ) {
        return count + 1;
      } else if (
        field === "pending_approval_type" &&
        Array.isArray(value) &&
        value.length > 0
      ) {
        return count + 1;
      }
      return count;
    }, 0);
    setFilterCount(count);
  }, [filters]);

  useEffect(() => {
    handleFilterCount();
  }, [handleFilterCount]);

  const handleFilterChange = useCallback(
    (
      key: keyof PurhcaseRequestFilters,
      value: PurhcaseRequestFilters[keyof PurhcaseRequestFilters]
    ) => {
      setFilters((prev: PurhcaseRequestFilters) => ({
        ...prev,
        [key]: value,
      }));
    },
    []
  );

  const filterMenu: FilterSidebarMenu[] = useMemo(() => {
    return [
      // {
      //   moduleType: ModuleType.PURCHASE_REQUEST,
      //   fieldType: FilterMenuType.DATERANGE,
      //   label: "Expected Payment Date",
      //   value: {
      //     startDate: filters.expected_payment_date_from || null,
      //     endDate: filters.expected_payment_date_to || null,
      //   },
      //   onChange: (selectedValues) => {
      //     setFilters((prev: PurhcaseRequestFilters) => {
      //       return {
      //         ...prev,
      //         expected_payment_date_from: selectedValues.startDate,
      //         expected_payment_date_to: selectedValues.endDate,
      //       };
      //     });
      //   },
      // },
      {
        moduleType: ModuleType.PURCHASE_REQUEST,
        fieldType: FilterMenuType.DATERANGE,
        label: "Created Date",
        value: {
          startDate: filters.created_date_from || null,
          endDate: filters.created_date_to || null,
        },
        onChange: (selectedValues) => {
          setFilters((prev: PurhcaseRequestFilters) => {
            return {
              ...prev,
              created_date_from: selectedValues.startDate,
              created_date_to: selectedValues.endDate,
            };
          });
        },
      },
      {
        moduleType: ModuleType.ADMIN,
        fieldType: FilterMenuType.SEARCH_BAR,
        subType: "search_admin",
        label: "Admin",
        placeholder: "Enter Admin name",
        value: filters.pr_created_by_admin_ids,
        onChange: (value: FilterValue[]) =>
          handleFilterChange("pr_created_by_admin_ids", value),
      },
      {
        moduleType: ModuleType.ORDER,
        fieldType: FilterMenuType.SEARCH_BAR,
        subType: "search_order",
        label: "Order",
        placeholder: "Search Order",
        value: filters.order_id,
        onChange: (value: FilterValue[]) =>
          handleFilterChange("order_id", value),
      },
      {
        moduleType: ModuleType.PURCHASE_REQUEST,
        fieldType: FilterMenuType.LOCAL_SEARCH_SINGLE_SELECT,
        label: "PR Status",
        placeholder: "Select PR status",
        inlinePlaceholder: true,
        list: PR_STATUS_OPTIONS,
        value: filters.pr_status,
        onChange: (value: FilterValue[]) =>
          handleFilterChange("pr_status", value),
      },
      {
        moduleType: ModuleType.PURCHASE_REQUEST,
        fieldType: FilterMenuType.LOCAL_SEARCH_MULTI_SELECT,
        label: "Pending Approval",
        placeholder: "Select pending type",
        list: PURCHASE_REQUEST_PENDING_APPROVAL_OPTIONS,
        value: PURCHASE_REQUEST_PENDING_APPROVAL_OPTIONS.filter((option) =>
          filters.pending_approval_type?.includes(
            option.id as PurchaseRequestPendingApprovalType,
          ),
        ),
        onChange: (selectedValues: FilterValue[]) => {
          handleFilterChange(
            "pending_approval_type",
            selectedValues.map(
              (item) => item.id as PurchaseRequestPendingApprovalType,
            ),
          );
        },
      },
      {
        moduleType: ModuleType.PURCHASE_REQUEST,
        fieldType: FilterMenuType.LOCAL_SEARCH_MULTI_SELECT,
        label: "Approved",
        placeholder: "Select approved type",
        list: PURCHASE_REQUEST_APPROVED_OPTIONS,
        value: PURCHASE_REQUEST_APPROVED_OPTIONS.filter((option) =>
          filters.approved_type?.includes(
            option.id as PurchaseRequestApprovedType,
          ),
        ),
        onChange: (selectedValues: FilterValue[]) => {
          handleFilterChange(
            "approved_type",
            selectedValues.map(
              (item) => item.id as PurchaseRequestApprovedType,
            ),
          );
        },
      },
    ];

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const handleApplyFilters = () => {
    setParams(filters);
    handleFilterToggle();
  };

  const handleClearFilters = () => {
    const clearedFilters: PurhcaseRequestFilters = {
      pr_created_by_admin_ids: [],
      order_id: [],
      page_no: 1,
      search_text: undefined,
      limit: initialParams.limit,
      sort_by: "created_date",
      sort_type: SortType.DESC,
      created_date_from: undefined,
      created_date_to: undefined,
      expected_payment_date_from: undefined,
      expected_payment_date_to: undefined,
      pending_approval_type: [],
      approved_type: [],
      pr_status: [],
    };
    setFilters(clearedFilters);
    setParams(clearedFilters);
    handleFilterToggle();
  };

  useEffect(() => {
    if (userStore.hasPermission(EmployeeRoles.PR_PO_VIEW)) {
      setIsLoading(true);
      purchaseRequestStore
        .getPurchaseRequestListing(params)
        .catch((err: any) =>
          showError(
            "Error",
            err?.response?.data?.message ||
            "Something went wrong! Please try again later."
          )
        )
        .finally(() => {
          setIsLoading(false);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  const handleSearch = (query: string) => {
    if (query.trim().length > 0) {
      setSearchTerm(query);
    } else {
      setSearchTerm("");
      setParams((prev: PurhcaseRequestFilters) => ({
        ...prev,
        search_text: undefined,
        page_no: 1,
      }));
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Enter") {
      if (searchTerm.trim().length > 0) {
        setParams((prev: PurhcaseRequestFilters) => ({
          ...prev,
          search_text: searchTerm,
          page_no: 1,
        }));
      } else {
        setSearchTerm("");
        setParams((prev: PurhcaseRequestFilters) => ({
          ...prev,
          search_text: "",
          page_no: 1,
        }));
      }
    }
  };

  const handlePageChange = (
    _event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    setParams((prev: PurhcaseRequestFilters) => ({
      ...prev,
      page_no: newPage,
    }));
  };

  const handleRowsPerPageChange = (newLimit: number) => {
    setParams((prev: PurhcaseRequestFilters) => ({
      ...prev,
      limit: newLimit,
      page_no: 1,
    }));
  };

  const handleSortChange = (column: string, sortType: SortType) => {
    setParams((prev: PurhcaseRequestFilters) => ({
      ...prev,
      sort_by: column,
      sort_type: sortType,
      page_no: 1,
    }));
  };

  const handleFilterToggle = () => {
    setOpenFilter(!openFilter);
  };

  const handleApprove = (prId: number, team: PurchaseRequestRejectedBy) => {
    setApprovePopup({ open: true, prId, team });
  };

  const handleApproveConfirm = (prId: number) => {
    const team = approvePopup.team!;
    purchaseRequestStore
      .approveRequest(prId, team, userStore)
      .then(() => {
        showSuccess("Request approved successfully");
        setApprovePopup({ open: false, prId: null, team: null });
      })
      .catch((err) => {
        showError(
          "Error",
          err.response?.data?.message ||
          "Something went wrong! Please try again later."
        );
        setApprovePopup({ open: false, prId: null, team: null });
      });
  };

  const handleReject = (prId: number, team: PurchaseRequestRejectedBy) => {
    setRejectPopup({ open: true, prId, team });
  };

  const handleCancelPR = (prId: number) => {
    const pr = purchaseRequestStore.prlist.find((item) => item.id === prId);

    if (pr && pr.active_po === 1) {
      showError(
        "Error",
        "Unable to cancel the PR because it has an active PO assigned. Please close the PO before proceeding with the PR cancellation."
      );
      return;
    }

    setCancelPRPopup({ open: true, prId });
  };

  const handleCancelPRConfirm = (prId: number, cancellationReason?: string) => {
    purchaseRequestStore
      .cancelPurchaseRequest(prId, userStore, cancellationReason)
      .then(() => {
        showSuccess("Request cancelled successfully");
        setCancelPRPopup({ open: false, prId: null });
      })
      .catch((err) => {
        showError(
          "Error",
          err.response?.data?.message ||
          "Something went wrong! Please try again later."
        );
        setCancelPRPopup({ open: false, prId: null });
      })
      .finally(() => {
        setCancelPRPopup({ open: false, prId: null });
      });
  };

  const handleRejectConfirm = (prId: number, rejectionReason?: string) => {
    const team = rejectPopup.team!;
    purchaseRequestStore
      .rejectRequest(prId, team, userStore, rejectionReason)
      .then(() => {
        showSuccess("Request rejected successfully");
        setRejectPopup({ open: false, prId: null, team: null });
      })
      .catch((err) => {
        showError(
          "Error",
          err.response?.data?.message ||
          "Something went wrong! Please try again later."
        );
        setRejectPopup({ open: false, prId: null, team: null });
      });
  };

  const handleGeneratePO = (prId: number) => {
    purchaseRequestStore
      .generatePO(prId)
      .then((response) => {
        showSuccess("PO generated successfully");
        purchaseRequestStore.prlist = purchaseRequestStore.prlist.map((pr) =>
          pr.id === prId
            ? {
              ...pr,
              po_ids: pr.po_ids
                ? `${pr.po_ids} / ${response.result}`
                : String(response.result)
            }
            : pr
        );
      })
      .catch((err) => {
        showError(
          "Error",
          err.response?.data?.message ||
          "Something went wrong! Please try again later."
        );
      });
  };

  useEffect(() => {
    const updateQueryParams = () => {
      const queryParams = new URLSearchParams();

      Object.entries(params).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          if (
            (key === "pending_approval_type" || key === "approved_type") &&
            value.length > 0
          ) {
            queryParams.append(key, value.join(","));
          } else if (
            value.length > 0 &&
            typeof value[0] === "object" &&
            "id" in value[0] &&
            "value" in value[0]
          ) {
            queryParams.append(
              key,
              value
                .map((item: FilterValue) => `${item.id}:${item.value}`)
                .join(",")
            );
          }
        } else if (
          key === "expected_payment_date_from" &&
          value &&
          dayjs.isDayjs(value)
        ) {
          if (value.isValid()) {
            queryParams.append(
              "expected_payment_date_from",
              value.toISOString()
            );
          }
        } else if (
          key === "expected_payment_date_to" &&
          value &&
          dayjs.isDayjs(value)
        ) {
          if (value.isValid()) {
            queryParams.append("expected_payment_date_to", value.toISOString());
          }
        } else if (
          key === "created_date_from" &&
          value &&
          dayjs.isDayjs(value)
        ) {
          if (value.isValid()) {
            queryParams.append("created_date_from", value.toISOString());
          }
        } else if (key === "created_date_to" && value && dayjs.isDayjs(value)) {
          if (value.isValid()) {
            queryParams.append("created_date_to", value.toISOString());
          }
        } else if (value !== undefined && value !== null) {
          queryParams.append(key, String(value));
        }
      });

      const queryString = queryParams.toString();
      navigate({ search: queryString ? `?${queryString}` : "" }, { replace: true });
    };
    updateQueryParams();
  }, [params, navigate]);

  return (
    <>
      {isLoading ? (
        <Container
          sx={{
            minHeight: "80vh",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <CircularProgress />
        </Container>
      ) : (
        // <Container maxWidth="xl">
        <Box>
          <Paper>
            {userStore.hasPermission(EmployeeRoles.PR_PO_VIEW) ? (
              <Box
                sx={{
                  marginTop: "0.6rem",
                  pt: 5,
                  pl: 5,
                  pr: 5,
                  pb: 1,
                  boxShadow:
                    "rgba(0, 0, 0, 0.25) 0px 0.0625em 0.0625em, rgba(0, 0, 0, 0.25) 0px 0.125em 0.5em, rgba(255, 255, 255, 0.1) 0px 0px 0px 1px inset",
                  borderRadius: "4px",
                  minHeight: window.innerHeight - 110,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                  }}
                >
                  <CustomTypography variant="heading3" fontWeight={700}>
                    Purchase Requests
                  </CustomTypography>
                </Box>

                <Box
                  sx={{
                    mt: 3,
                    mb: 3,
                    display: "flex",
                    justifyContent: "space-between",
                    gap: 4,
                  }}
                >
                  <CustomTextField
                    placeholder={
                      "Search by PR Id, Order Id, PO Id, Vendor name, -1 for Swageazy Inventory"
                    }
                    value={searchTerm}
                    onSearch={(query) => handleSearch(query)}
                    onKeyDown={handleKeyDown}
                    width={"100%"}
                  />
                  <Box sx={{ position: "relative", display: "inline-block" }}>
                    <AppButton
                      variant="grayscale"
                      label="Filter"
                      startIcon={<FilterAltOutlinedIcon />}
                      onClick={() => {
                        handleFilterToggle();
                      }}
                      customStyles={{ width: "130px" }}
                    />
                    {filterCount > 0 && (
                      <Badge
                        color="primary"
                        badgeContent={filterCount}
                        sx={{
                          position: "absolute",
                          top: 2,
                          right: 2,
                          transform: "translate(50%, -50%)",
                        }}
                      />
                    )}
                  </Box>
                </Box>

                {isLoading && (
                  <Container
                    sx={{
                      minHeight: "60vh",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                  >
                    <CircularProgress />
                  </Container>
                )}

                <PrListingTable
                  maxHeight={window.innerHeight - 340}
                  limit={params.limit || purchaseRequestStore.limit}
                  page_no={params.page_no || 1}
                  total_count={purchaseRequestStore.total_count}
                  total_pages={purchaseRequestStore.total_pages}
                  sortBy={params.sort_by}
                  sortType={params.sort_type}
                  data={purchaseRequestStore.prlist}
                  onPageChange={handlePageChange}
                  onRowsPerPageChange={handleRowsPerPageChange}
                  onSortChange={handleSortChange}
                  onApprove={handleApprove}
                  onReject={handleReject}
                  onCancelPR={handleCancelPR}
                  onGeneratePO={handleGeneratePO}
                />

                {openFilter && (
                  <FilterSidebar
                    isOpen={openFilter}
                    items={filterMenu}
                    onApply={handleApplyFilters}
                    handleClear={handleClearFilters}
                    toggleFilter={handleFilterToggle}
                  />
                )}
              </Box>
            ) : (
              <PermissionBlockLabel />
            )}
          </Paper>
        </Box>
      )}

      {/* Approve Confirmation Popup */}
      <ConfirmationPopup
        id={approvePopup.prId || 0}
        open={approvePopup.open}
        title="Approve Purchase Request"
        content="Are you sure you want to approve this purchase request? This action cannot be undone."
        type="standard"
        onConfirm={handleApproveConfirm}
        onCancel={() =>
          setApprovePopup({ open: false, prId: null, team: null })
        }
      />

      {/* Reject Confirmation Popup */}
      <ConfirmationPopup
        id={rejectPopup.prId || 0}
        open={rejectPopup.open}
        title="Reject Purchase Request"
        content="Are you sure you want to reject this purchase request? Please provide a reason for rejection."
        type="form"
        inputRequired={true}
        inputLabel="Rejection Reason"
        inputType="text"
        onConfirm={handleRejectConfirm}
        onCancel={() => setRejectPopup({ open: false, prId: null, team: null })}
      />

      <ConfirmationPopup
        id={cancelPRPopup.prId || 0}
        open={cancelPRPopup.open}
        title="Cancel Purchase Request"
        content="Are you sure you want to cancel this purchase request? Please provide a reason for cancellation."
        type="form"
        inputRequired={true}
        inputLabel="Cancellation Reason"
        inputType="text"
        onConfirm={handleCancelPRConfirm}
        onCancel={() => setCancelPRPopup({ open: false, prId: null })}
      />
    </>
  );
};
export default React.memo(PurchaseRequest);
